import type { RetailPartner } from '../../../mocks/retailers';

interface PartnerCardProps {
  partner: RetailPartner;
}

export default function PartnerCard({ partner }: PartnerCardProps) {
  const typeColors: Record<string, string> = {
    supermarket: 'bg-amber-100 text-amber-800',
    health: 'bg-green-100 text-green-800',
    grocery: 'bg-orange-100 text-orange-800',
    online: 'bg-sky-100 text-sky-800',
    distributor: 'bg-purple-100 text-purple-800',
  };

  const typeLabels: Record<string, string> = {
    supermarket: 'Supermarket',
    health: 'Health Store',
    grocery: 'Grocery Chain',
    online: 'Online Platform',
    distributor: 'Distributor',
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-300 cursor-pointer group">
      <div className="relative h-44 overflow-hidden">
        <img
          src={partner.logo}
          alt={partner.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        {partner.badge && (
          <div className="absolute top-3 left-3 bg-amber-900 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
            {partner.badge}
          </div>
        )}
        <div className="absolute bottom-3 left-3">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${typeColors[partner.type] || 'bg-gray-100 text-gray-700'}`}>
            {typeLabels[partner.type] || partner.type}
          </span>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-base font-bold text-gray-900 mb-1">{partner.name}</h3>
        <div className="flex items-center gap-1 text-gray-500 mb-3">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-line text-xs"></i>
          </div>
          <span className="text-xs">{partner.city}</span>
          <span className="text-xs text-gray-400 ml-1">· {partner.storeCount} {partner.storeCount === 1 ? 'location' : 'locations'}</span>
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {partner.products.map((product) => (
            <span key={product} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
              {product}
            </span>
          ))}
        </div>

        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500 font-medium mb-1">Available at:</p>
          <div className="flex flex-wrap gap-1">
            {partner.locations.slice(0, 3).map((loc) => (
              <span key={loc} className="text-xs text-gray-600">
                {loc}{partner.locations.indexOf(loc) < Math.min(2, partner.locations.length - 1) ? ',' : ''}
              </span>
            ))}
            {partner.locations.length > 3 && (
              <span className="text-xs text-amber-700 font-medium">+{partner.locations.length - 3} more</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
